import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import SEOHead from "../../components/shared/SEOHead.jsx";
import Breadcrumbs from "../../components/shared/Breadcrumbs.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import { ABOUT_PATH, ORGAS_PATH } from "./paths.js";
import { ORGAS_CONTENT } from "./orgasContent.js";
import { getExternalOrgaEvents } from "../../utils/content.js";

const INFO_PATH = { de: "infos", en: "info" };

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ background: "var(--color-bg-raised)", borderRadius: "var(--radius-card)", padding: "24px 26px" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
          width: "100%",
          background: "none",
          border: "none",
          padding: 0,
          margin: 0,
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
          color: "var(--color-cream)",
        }}
      >
        <span style={{ fontSize: "16px" }}>{question}</span>
        <span style={{ fontSize: "18px", color: open ? "var(--color-gold)" : "rgba(254,250,239,0.5)", flex: "none" }}>{open ? "−" : "+"}</span>
      </button>
      {open &&
        answer.split("\n\n").map((paragraph, i) => (
          <p key={i} style={{ fontSize: "14px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: i === 0 ? "14px 0 0" : "10px 0 0" }}>
            {paragraph}
          </p>
        ))}
    </div>
  );
}

export default function OrgasPage({ lang }) {
  const { t } = useTranslation();
  const c = ORGAS_CONTENT[lang];
  const caseStudyEvents = getExternalOrgaEvents(lang);
  const otherLang = lang === "de" ? "en" : "de";
  const path = `/${ABOUT_PATH[lang]}/${ORGAS_PATH[lang]}`;
  const altPath = `/${ABOUT_PATH[otherLang]}/${ORGAS_PATH[otherLang]}`;
  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.ueberUns"), path: `/${ABOUT_PATH[lang]}` },
    { name: c.hero.eyebrow, path },
  ];

  return (
    <>
      <SEOHead lang={lang} path={path} altPath={altPath} title={c.hero.eyebrow} />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "64px", padding: "32px 40px 96px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />

          <section className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: "32px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "17px", color: "var(--color-gold)", margin: 0 }}>
                {c.hero.eyebrow}
              </p>
              <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", maxWidth: "22ch", margin: 0 }}>
                {c.hero.title}
              </h1>
              <p style={{ fontSize: "17px", lineHeight: 1.7, color: "var(--color-cream-soft)", maxWidth: "52ch", margin: 0 }}>{c.hero.body}</p>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "8px" }}>
                <Link to={`/${lang}/${INFO_PATH[lang]}`} className="tl-pill tl-pill-primary">
                  {c.hero.ctaPrimary}
                </Link>
                <Link to={`/${lang}/${ABOUT_PATH[lang]}`} className="tl-pill tl-pill-outline">
                  {c.hero.ctaSecondary}
                </Link>
              </div>
            </div>
            <div style={{ position: "relative", width: "100%", aspectRatio: "4 / 3", borderRadius: "var(--radius-card-lg)", overflow: "hidden" }}>
              <ResponsiveImage
                src="/assets/brand/for_orgas_hero.png"
                alt=""
                priority
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>{c.servicesTitle}</h2>
            <div className="tl-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
              {c.services.map((service) => (
                <div key={service.title} className="tl-card" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", color: "var(--color-cream)" }}>{service.title}</span>
                  <span style={{ fontSize: "14px", lineHeight: 1.7, color: "var(--color-cream-soft)" }}>{service.body}</span>
                </div>
              ))}
            </div>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>{c.processTitle}</h2>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {c.steps.map((step, i) => (
                <div
                  key={step.num}
                  className="tl-grid-3"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "64px 1fr 220px",
                    gap: "28px",
                    alignItems: "start",
                    padding: "24px 0",
                    borderTop: "1px solid rgba(254,250,239,0.12)",
                    borderBottom: i === c.steps.length - 1 ? "1px solid rgba(254,250,239,0.12)" : undefined,
                  }}
                >
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-gold)" }}>{step.num}</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <span style={{ fontSize: "17px", color: "var(--color-cream)" }}>{step.title}</span>
                    <span style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)", maxWidth: "60ch" }}>{step.body}</span>
                  </div>
                  <span style={{ fontSize: "14px", color: "var(--color-cream-faint)" }}>{step.duration}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "34px" }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)" }}>{c.fitTitle}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {c.fitItems.map((item) => (
                  <div key={item} style={{ display: "grid", gridTemplateColumns: "20px 1fr", gap: "12px", fontSize: "15px", lineHeight: 1.6, color: "rgba(10,9,7,0.75)" }}>
                    <span style={{ color: "#8a6520" }}>✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", background: "var(--color-bg-raised)", borderRadius: "var(--radius-card-lg)", padding: "34px" }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-cream)" }}>{c.notFitTitle}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {c.notFitItems.map((item) => (
                  <div key={item} style={{ display: "grid", gridTemplateColumns: "20px 1fr", gap: "12px", fontSize: "15px", lineHeight: 1.6, color: "rgba(254,250,239,0.68)" }}>
                    <span style={{ color: "rgba(254,250,239,0.4)" }}>✕</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>{c.caseStudiesTitle}</h2>
            {caseStudyEvents.length === 0 ? (
              <p className="placeholder" style={{ fontSize: "14px", color: "var(--color-cream-faint)", margin: 0 }}>{c.caseStudiesEmpty}</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "24px" }}>
                {caseStudyEvents.map((event) => {
                  const f = event.frontmatter;
                  return (
                    <Link
                      key={f.slug}
                      to={`/${lang}/events/${f.slug}`}
                      className="tl-card-light tl-hover-lift"
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      <ResponsiveImage
                        src={f.image}
                        alt={f.title}
                        variant="light"
                        style={{ width: "100%", height: "150px", objectFit: "cover", display: "block" }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "24px" }}>
                        <span style={{ fontSize: "12px", color: "rgba(10,9,7,0.55)" }}>
                          {f.orgaName ? `${c.caseStudyWith} ${f.orgaName} · ` : ""}
                          {new Date(f.date).getFullYear()}
                        </span>
                        <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", lineHeight: 1.25, color: "#0a0907" }}>{f.title}</span>
                        {f.caseStudyNote && (
                          <span style={{ fontSize: "13px", lineHeight: 1.6, color: "rgba(10,9,7,0.65)" }}>{f.caseStudyNote}</span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>{c.faqTitle}</h2>
            {c.faq.map(({ category, items }) => (
              <div key={category} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "17px", color: "var(--color-gold)" }}>
                  {category}
                </span>
                {items.map((item) => (
                  <FaqItem key={item.q} question={item.q} answer={item.a} />
                ))}
              </div>
            ))}
          </section>

          <section
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "40px",
              flexWrap: "wrap",
              border: "1px solid rgba(206,149,50,0.55)",
              borderRadius: "14px",
              padding: "36px 40px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", flex: "1 1 420px" }}>
              <p style={{ fontFamily: "var(--font-serif)", fontSize: "28px", lineHeight: 1.2, color: "var(--color-cream)", margin: 0 }}>{c.ctaTitle}</p>
              <p style={{ fontSize: "14px", lineHeight: 1.65, color: "var(--color-cream-soft)", maxWidth: "60ch", margin: 0 }}>{c.ctaBody}</p>
            </div>
            <Link to={`/${lang}/${INFO_PATH[lang]}`} className="tl-pill tl-pill-primary" style={{ padding: "16px 34px", fontSize: "16px" }}>
              {c.ctaButton}
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}
